import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import React, { memo, useState } from 'react';

import { disableWaapi } from '~/helpers/animate';
import useImageLoaded from '~/hooks/useImageLoaded';
import useRouteHandlePropChange from '~/hooks/useRouteHandlePropChange';
import useWindowResize from '~/hooks/useWindowResize';
import { isScreen3kMin } from '~/utils';

import styles from './ViewBackground.module.scss';

import type { Nullable } from '~/types/generics';

const bgAnimation = {
  transition: { duration: 0.3 },
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  onUpdate: disableWaapi,
};

const ViewBackground: React.FC = () => {
  const [bgClassName, setBgClassName] = useState<Nullable<string>>(null);
  const [bgImage, setBgImage] = useState<Nullable<string>>(null);
  const [bgVideo, setBgVideo] = useState<Nullable<string>>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(isScreen3kMin());

  const isImageLoaded = useImageLoaded(bgImage);

  useWindowResize(() => {
    setIsLargeScreen(isScreen3kMin());
  });

  useRouteHandlePropChange('bgClassName', (bgClassName) => {
    setBgClassName(bgClassName || null);
  });

  useRouteHandlePropChange('bgVideo', (bgVideo) => {
    setBgVideo(bgVideo || null);
  });

  useRouteHandlePropChange('bgImage', (bgImage) => {
    if (!bgImage) {
      return setBgImage(null);
    }

    if (typeof bgImage === 'string') {
      return setBgImage(bgImage || null);
    }

    if (isLargeScreen && bgImage[1]) {
      return setBgImage(bgImage[1]);
    }

    setBgImage(bgImage[0] || null);
  });

  return (
    <AnimatePresence>
      {isImageLoaded && (
        <motion.div {...bgAnimation} key={`bg_${bgImage}`}>
          <div
            className={classNames(styles.bg, bgClassName && styles[bgClassName])}
            style={{ backgroundImage: `url(${bgImage})` }}
          />
          {bgVideo && (
            <video className={styles.bgVideo} autoPlay loop muted playsInline>
              <source src={bgVideo} type="video/webm" />
            </video>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default memo(ViewBackground);
